"use client";

import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import React from "react";

type NavLinkItem = { href: string; label: string };

const NAV_LINKS: NavLinkItem[] = [
  { href: "#portfolio", label: "Portfolio" },
  { href: "#services", label: "Services" },
  { href: "#about", label: "About" },
  { href: "#process", label: "Process" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [activeHash, setActiveHash] = React.useState<string>("");

  React.useEffect(() => {
    // Initialize activeHash on client side only
    if (typeof window !== "undefined") {
      setActiveHash(window.location.hash || "");
    }
  }, []);

  React.useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    const handleHashChange = () => {
      if (typeof window !== "undefined") {
        setActiveHash(window.location.hash || "");
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const closeMenu = React.useCallback(() => setIsOpen(false), []);

  return (
    <nav
      aria-label="Main navigation"
      className={
        (isScrolled ? "shadow-sm " : "") +
        "bg-background/80 supports-backdrop-filter:bg-background/60 sticky top-0 z-50 w-full border-b backdrop-blur transition-shadow"
      }
    >
      <div className="container mx-auto flex h-14 items-center justify-between gap-2 px-4 sm:h-16 sm:gap-4 sm:px-6 lg:gap-6 lg:px-8">
        <Link
          href="/"
          className="focus-visible:ring-ring flex items-center gap-2 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:gap-3"
        >
          <Image
            src="/Logo_black.png"
            alt="GD Group Studio logo"
            width={200}
            height={100}
            className="h-16 w-auto object-contain sm:h-20 md:h-24 lg:h-28"
            priority
          />
          <span className="sr-only">GD Group Studio</span>
        </Link>

        <div className="flex items-center gap-4 md:gap-6">
          {/* Desktop nav */}
          <ul className="hidden items-center gap-6 md:flex" role="list">
            {NAV_LINKS.map(link => {
              const isActive =
                activeHash === link.href || (link.href === "#home" && activeHash === "");
              return (
                <li key={link.href} role="none">
                  <a
                    href={link.href}
                    onClick={e => {
                      e.preventDefault();
                      const target = document.querySelector(link.href);
                      if (target) {
                        const element = target as HTMLElement;
                        const offsetTop = element.offsetTop - 80; // Account for navbar height
                        window.scrollTo({
                          top: offsetTop,
                          behavior: "smooth",
                        });
                        window.history.pushState(null, "", link.href);
                        setActiveHash(link.href);
                        // Focus management for accessibility
                        element.setAttribute("tabindex", "-1");
                        element.focus({ preventScroll: true });
                        element.removeAttribute("tabindex");
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const target = document.querySelector(link.href);
                        if (target) {
                          const element = target as HTMLElement;
                          const offsetTop = element.offsetTop - 80; // Account for navbar height
                          window.scrollTo({
                            top: offsetTop,
                            behavior: "smooth",
                          });
                          window.history.pushState(null, "", link.href);
                          setActiveHash(link.href);
                          element.setAttribute("tabindex", "-1");
                          element.focus({ preventScroll: true });
                          element.removeAttribute("tabindex");
                        }
                      }
                    }}
                    aria-current={isActive ? "page" : undefined}
                    className={`text-muted-foreground hover:text-foreground after:bg-primary focus-visible:ring-ring relative text-sm transition-all duration-300 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:transition-all after:duration-300 hover:after:w-full focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${
                      isActive ? "text-foreground after:w-full" : ""
                    }`}
                  >
                    {link.label}
                  </a>
                </li>
              );
            })}
          </ul>
          <a
            href="#contact"
            aria-label="Book a session - navigate to contact section"
            onClick={e => {
              e.preventDefault();
              const target = document.querySelector("#contact");
              if (target) {
                target.scrollIntoView({ behavior: "smooth" });
                (target as HTMLElement).focus();
              }
            }}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                const target = document.querySelector("#contact");
                if (target) {
                  target.scrollIntoView({ behavior: "smooth" });
                  (target as HTMLElement).focus();
                }
              }
            }}
            className="focus-visible:ring-ring hidden focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:block"
          >
            <Button size="sm" className="transition-transform duration-300 hover:scale-105">
              Book now
            </Button>
          </a>
          {/* Mobile menu button */}
          <button
            type="button"
            className="hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-all duration-300 outline-none hover:scale-105 focus-visible:ring-[3px] md:hidden"
            aria-controls="mobile-menu"
            aria-expanded={isOpen}
            onClick={() => setIsOpen(v => !v)}
          >
            <span className={`transition-transform duration-300 ${isOpen ? "rotate-90" : ""}`}>
              {isOpen ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  className="size-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  className="size-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </span>
            <span className="sr-only">Toggle menu</span>
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      <div
        id="mobile-menu"
        role="menu"
        aria-label="Mobile navigation menu"
        className={
          isOpen
            ? "bg-background/95 animate-in slide-in-from-top-2 border-t backdrop-blur transition-all duration-300 md:hidden"
            : "hidden md:hidden"
        }
      >
        <div className="container mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <nav aria-label="Mobile navigation links" role="list">
            <div className="flex flex-col gap-3 sm:gap-4">
              {NAV_LINKS.map(link => {
                const isActive =
                  activeHash === link.href || (link.href === "#home" && activeHash === "");
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    role="menuitem"
                    aria-current={isActive ? "page" : undefined}
                    onClick={e => {
                      e.preventDefault();
                      window.location.hash = link.href;
                      setActiveHash(link.href);
                      closeMenu();
                      const target = document.querySelector(link.href);
                      if (target) {
                        target.scrollIntoView({ behavior: "smooth" });
                        (target as HTMLElement).focus();
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === "Escape") {
                        closeMenu();
                        // Return focus to menu button
                        const menuButton = document.querySelector(
                          '[aria-controls="mobile-menu"]'
                        ) as HTMLElement;
                        if (menuButton) menuButton.focus();
                      } else if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        window.location.hash = link.href;
                        setActiveHash(link.href);
                        closeMenu();
                        const target = document.querySelector(link.href);
                        if (target) {
                          target.scrollIntoView({ behavior: "smooth" });
                          (target as HTMLElement).focus();
                        }
                      }
                    }}
                    className="text-muted-foreground hover:text-foreground focus-visible:ring-ring text-sm transition-all duration-300 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  >
                    {link.label}
                  </a>
                );
              })}
              <a
                href="#contact"
                role="menuitem"
                aria-label="Book a session - navigate to contact section"
                onClick={e => {
                  e.preventDefault();
                  closeMenu();
                  const target = document.querySelector("#contact");
                  if (target) {
                    target.scrollIntoView({ behavior: "smooth" });
                    (target as HTMLElement).focus();
                  }
                }}
                onKeyDown={e => {
                  if (e.key === "Escape") {
                    closeMenu();
                    const menuButton = document.querySelector(
                      '[aria-controls="mobile-menu"]'
                    ) as HTMLElement;
                    if (menuButton) menuButton.focus();
                  } else if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    closeMenu();
                    const target = document.querySelector("#contact");
                    if (target) {
                      target.scrollIntoView({ behavior: "smooth" });
                      (target as HTMLElement).focus();
                    }
                  }
                }}
                className="focus-visible:ring-ring pt-2 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <Button className="w-full" size="sm">
                  Book now
                </Button>
              </a>
            </div>
          </nav>
        </div>
      </div>
    </nav>
  );
}
